<template>
  <modal
    name="study-goal-set"
    :adaptive="true"
    :classes="['v--modal', 'study-goal-set-modal']"
    :click-to-close="true"
    height="auto"
    @before-open="beforeOpen"
  >
    <div class="modal-content">
      <p class="title">
        Choose Your Question Quantity
      </p>
      <p>Set a reasonable goal to start. You can change it any time in your profile.</p>
      <div class="goal-selection">
        <div
          v-for="dailyGoal in suggestedGoals"
          :key="`goal-${dailyGoal.goal}`"
          class="goal-choice"
        >
          <input
            :id="`goal-${dailyGoal.goal}`"
            v-model="goal"
            class="goal-input"
            type="radio"
            name="study-goal"
            :value="dailyGoal.goal"
            @change="updateGoal"
          >
          <label
            class="goal-label"
            :for="`goal-${dailyGoal.goal}`"
          >
            {{ dailyGoal.goal }}
          </label>
        </div>
        <p
          class="time-estimate"
          data-test="time-estimate"
        >
          {{ timeEstimate }}
        </p>
      </div>
      <mastery-button
        class="save-goal-button"
        data-test="save-goal-button"
        :disabled="submitting"
        @mastery-button-clicked="saveGoal"
      >
        {{ saveMyGoalText }}
      </mastery-button>
      <button
        class="set-custom-goal-button"
        @click="setCustomGoal"
      >
        Set Custom Goal
      </button>
    </div>
  </modal>
</template>

<script>
import MasteryButton from '@/components/ui/masteryButton';
import { SET_GOAL_KEY, setChecklistData } from '@/utils/checklist';
import RateLimitMixin from '@/mixins/rate-limit';

// These should be passed from the dashboard config.
// These exist as a fall back just in case data is missing or malformed
const exampleGoals = [
  {
    goal:10,
    timeEstimate:'Approximately 10-15 minutes',
  },
  {
    goal:50,
    timeEstimate:'Approximately 1 hour',
  },
  {
    goal: 100,
    timeEstimate: 'Approximately 2 hours',
  },
];

export default {
  components: {MasteryButton},
  mixins: [RateLimitMixin],
  data() {
    return {
      goal: 10,
      widgetGoals: [],
      submitting: false,
    };
  },
  computed: {
    selectedGoalRequiresSubscription() {
      return this.shouldShowRateLimit && this.goal > this.rateLimitingCycleCredits;
    },
    saveMyGoalText() {
      if (this.selectedGoalRequiresSubscription) {
        return 'Save and Subscribe';
      } else if (this.shouldShowRateLimit) {
        return 'Save';
      } else {
        return 'Save My Goal';
      }
    },
    selectedGoal() {
      return this.suggestedGoals.find(goal => goal.goal == this.goal);
    },
    suggestedGoals() {
      return this.widgetGoals || exampleGoals;
    },
    timeEstimate() {
      if (this.selectedGoalRequiresSubscription) {
        return '(Requires subscription.)';
      } else {
        return this.selectedGoal?.timeEstimate;
      }
    },
  },
  methods: {
    beforeOpen(event) {
      if (event?.params) {
        this.widgetGoals = event.params.goals;
      }
    },
    close() {
      this.$modal.hide('study-goal-set');
    },
    saveGoal() {
      this.submitting = true;
      this.$store.dispatch('session/updateUserProfile', { studyGoal: this.goal }).then(() => {
        this.$analytics.events.setDailyGoal('Today Widget');
        this.$analytics.events.savedStudyGoalFromModal('Saved Study Goal From Modal', { 'Number Of Questions': this.goal});
        setChecklistData(this.$store, SET_GOAL_KEY, this.$notify);
        this.close();
        this.submitting = false;
      });
    },
    setCustomGoal() {
      this.close();
      this.$analytics.events.viewedSetCustomStudyGoalFromModal();
      this.$store.commit('ui/setProfileModalShown', 'editProfileModal');
    },
    updateGoal() {
      this.$analytics.events.updatedStudyGoalFromModal('Updated Study Goal From Modal', { 'Number Of Questions': this.goal});
    },
  },
};
</script>

<style lang="scss">
.vm--container:has(.study-goal-set-modal) {
  background: rgba(0, 0, 0, .90);

  .vm--modal {
    background: transparent;
    box-shadow: none;
  }
}

.study-goal-set-modal {
  $success-green: #00b46d;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal-content {
    max-width: rem-calc(376);
    text-align: center;
    color: #ffffff;
    line-height: 1.5;

    .title {
      font-size: rem-calc(26);
      font-weight: $bold;
      line-height: 1.23;
    }

    .goal-selection {
      margin-top: rem-calc(70);
      margin-bottom: rem-calc(60);

      .goal-choice {
        display: inline-block;
        width: rem-calc(90);
        height: rem-calc(90);
        border-radius: 100%;

        // Targeting the 3rd option, no margin is needed there
        &:not(:nth-child(3)) {
          margin-right: rem-calc(24);
        }

        .goal-input {
          display: none;
        }

        .goal-label {
          cursor: pointer;
          display: inline-block;
          font-weight: $bold;
          font-size: rem-calc(30);
          height: 100%;
          width: 100%;
          box-sizing: border-box;
          padding-top: rem-calc(20); // 88 height - 41 height on page / 2 for top/bottom and adjusted by eye
          border-radius: 100%;
          border: 2px solid #ffffff;
        }

        .goal-input:checked ~ .goal-label {
          background: $success-green;
          border-color: $success-green;
        }
      }

      .time-estimate {
        font-size: rem-calc(12);
      }
    }

    .save-goal-button {
      text-transform: uppercase;

      // same as .save-goal-button but with selectors specific to override default mastery-button
      &.mastery-button {
        width: 80%;
        background-color: $success-green;
        box-shadow: 0 2px 2px 0 rgba(0, 0, 0, .25);
      }
    }

    .set-custom-goal-button {
      background: transparent;
      border: none;
      color: inherit;
      display: block;
      margin: 0 auto;
      margin-top: rem-calc(24);

      &:hover {
        text-decoration: underline;
      }
    }

  }

}
</style>
